import { useEffect, useState } from 'react';
import { currentBuildId, fetchVersionInfo, isNewBuild, VersionInfo } from './versionCheck';

/** How often the app asks for a new build while the tab is visible. */
export const VERSION_POLL_MS = 5 * 60 * 1000;

/**
 * Asks `/app/version.json` every 5 minutes while the tab is visible, and one time when the tab
 * comes back. Answers the `version.json` of a new build when one is live, else null, and then
 * stops. Nothing reloads by itself: only the click on "Refresh" (or "Reload" in the What's new
 * update panel) reloads, so an unsent draft is never lost.
 */
export function useVersionPoll(enabled: boolean, fetchImpl: typeof fetch = fetch): VersionInfo | null {
  const [newBuild, setNewBuild] = useState<VersionInfo | null>(null);

  useEffect(() => {
    if (!enabled || newBuild !== null) {
      return;
    }
    const own = currentBuildId();
    let stopped = false;
    const check = () => {
      if (document.visibilityState !== 'visible') {
        return;
      }
      fetchVersionInfo(fetchImpl).then(
        (info) => {
          if (!stopped && info !== null && isNewBuild(info.buildId, own)) {
            setNewBuild(info);
          }
        },
        () => undefined
      );
    };
    const timer = window.setInterval(check, VERSION_POLL_MS);
    const onVisible = () => {
      if (document.visibilityState === 'visible') {
        check();
      }
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      stopped = true;
      window.clearInterval(timer);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [enabled, fetchImpl, newBuild]);

  return newBuild;
}
